import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, Calculator } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { scrollToId } from "../lib/site";

const PLANS = [
  {
    id: "starter",
    name: "Starter AI Site",
    monthly: 49,
    tagline: "A stunning website, built and managed by AI.",
    features: ["AI-built website from your Google profile", "Hosting, SSL & maintenance included", "Booking & contact forms", "Monthly AI content refresh"],
    target: "preview",
    cta: "Start with free preview",
  },
  {
    id: "pro",
    name: "Pro Voice + Site",
    monthly: 149,
    tagline: "The full front desk: website plus 24/7 AI voice agent.",
    features: ["Everything in Starter", "24/7 AI voice receptionist", "Calendar booking & SMS confirmations", "Call recordings & transcripts", "Emergency dispatch rules"],
    target: "voice-inquiry",
    cta: "Design my voice agent",
    popular: true,
  },
  {
    id: "enterprise",
    name: "Enterprise Scale",
    monthly: 399,
    tagline: "Multi-location brands and high call volumes.",
    features: ["Everything in Pro", "Multi-location management", "Outbound reminder campaigns", "Custom integrations & API", "Dedicated success engineer"],
    target: "voice-inquiry",
    cta: "Talk to our team",
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(true);
  const [missed, setMissed] = useState(12);
  const recovered = Math.round(missed * 4.3 * 0.35 * 120);

  const price = (m) => (annual ? Math.round((m * 10) / 12) : m);

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 md:py-36 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHeading
            testId="pricing-heading"
            kicker="Pricing"
            title="Cheaper than one missed job"
            sub="Every plan starts with a free preview. Cancel anytime."
          />
          <Reveal delay={0.2}>
            <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 p-1.5" data-testid="pricing-billing-toggle">
              {["Monthly", "Annual"].map((label) => {
                const isAnnual = label === "Annual";
                const on = annual === isAnnual;
                return (
                  <button
                    key={label}
                    data-testid={`pricing-toggle-${label.toLowerCase()}`}
                    onClick={() => setAnnual(isAnnual)}
                    className={`rounded-full px-5 py-2 text-sm font-semibold transition-[background-color,color] duration-300 ${
                      on ? "bg-cyan-400 text-[#07090E]" : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    {label}
                    {isAnnual && <span className={on ? "text-[#07090E]/70" : "text-cyan-400"}> −2 mo</span>}
                  </button>
                );
              })}
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.12}>
              <div
                data-testid={`pricing-card-${p.id}`}
                className={`glow-card relative h-full rounded-2xl p-8 flex flex-col ${
                  p.popular ? "border-cyan-400/40 shadow-[0_0_60px_rgba(0,240,255,0.1)]" : ""
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3.5 left-8 inline-flex items-center gap-1.5 rounded-full bg-cyan-400 px-3.5 py-1 text-xs font-bold text-[#07090E]">
                    <Sparkles className="h-3 w-3" /> MOST POPULAR
                  </span>
                )}
                <h3 className="font-display text-xl font-semibold text-slate-50">{p.name}</h3>
                <p className="mt-2 text-sm text-slate-400">{p.tagline}</p>
                <div className="mt-6 flex items-baseline gap-2">
                  <motion.span
                    key={String(annual)}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="font-display text-5xl font-extrabold tracking-tight text-slate-50"
                    data-testid={`pricing-price-${p.id}`}
                  >
                    ${price(p.monthly)}
                  </motion.span>
                  <span className="text-sm text-slate-500">/ month{annual && ", billed annually"}</span>
                </div>
                <ul className="mt-7 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`pricing-cta-${p.id}`}
                  onClick={() => scrollToId(p.target)}
                  className={`mt-8 w-full rounded-xl px-6 py-3.5 text-sm font-bold transition-[background-color,box-shadow] duration-300 ${
                    p.popular
                      ? "bg-cyan-400 text-[#07090E] hover:bg-cyan-300 hover:shadow-[0_0_36px_rgba(0,240,255,0.4)]"
                      : "border border-white/15 bg-white/5 text-slate-200 hover:border-cyan-400/50 hover:text-cyan-200"
                  }`}
                >
                  {p.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-10">
          <div className="glow-card rounded-2xl p-8 sm:p-10 flex flex-col lg:flex-row items-center gap-10" data-testid="roi-calculator">
            <div className="flex items-center gap-4 shrink-0">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-500/15 ring-1 ring-violet-400/40">
                <Calculator className="h-6 w-6 text-violet-300" />
              </span>
              <div>
                <h3 className="font-display text-lg font-semibold text-slate-50">Missed-call ROI</h3>
                <p className="text-sm text-slate-500">Avg. local job value: $120 · capture rate 35%</p>
              </div>
            </div>
            <div className="flex-1 w-full">
              <div className="flex justify-between text-xs font-mono text-slate-500 mb-3">
                <span>Missed calls / week</span>
                <span className="text-cyan-300 font-semibold" data-testid="roi-missed-value">{missed}</span>
              </div>
              <input
                data-testid="roi-slider"
                type="range"
                min="0"
                max="40"
                value={missed}
                onChange={(e) => setMissed(Number(e.target.value))}
                className="w-full h-1.5 appearance-none rounded-full bg-white/10 accent-cyan-400 cursor-pointer"
                aria-label="Missed calls per week"
              />
            </div>
            <div className="text-center lg:text-right shrink-0">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Recovered / month</p>
              <motion.p
                key={recovered}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="font-display text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-violet-400"
                data-testid="roi-recovered-value"
              >
                ${recovered.toLocaleString()}
              </motion.p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
